/**
 * Standalone event mixin, replacing the MooTools Events prototype.
 *
 * Usage:
 *    Object.append(Thing.prototype, EventsFunctions);
 *
 * Each instance lazily initialises its own `$events` map the first time
 * an event method is called, avoiding the shared-prototype bug described
 * in the original MooTools version.
 */

type EventHandler = (...args: any[]) => void;

interface EventHandlerWithInternal extends EventHandler {
   internal?: boolean;
}

/**
 * Strips a leading "on" prefix with an uppercase letter, lowering the
 * first character.  e.g. "onSuccess" → "success", "click" → "click".
 */
function removeOn(string: string): string {
   return string.replace(/^on([A-Z])/, (_full, first) => first.toLowerCase());
}

/**
 * Converts an argument to an array, matching MooTools' `Array.convert`:
 * - null/undefined → []
 * - already an array → returned as-is
 * - single value → wrapped in []
 * - array-like (arguments, NodeList) → Array.from
 */
function toArray(value: any): any[] {
   if (value == null) return [];
   if (Array.isArray(value)) return value;
   if (typeof value === 'object' && typeof value.length === 'number') {
      return Array.from(value);
   }
   return [value];
}

export const EventsFunctions = {
   addEvent(this: any, type: string, fn: EventHandlerWithInternal, internal?: boolean) {
      type = removeOn(type);
      if (!this.$events) {
         this.$events = {};
      }

      const list: EventHandlerWithInternal[] = this.$events[type] || [];
      if (!list.includes(fn)) {
         list.push(fn);
      }
      this.$events[type] = list;

      if (internal) {
         fn.internal = true;
      }
      return this;
   },

   addEvents(this: any, events: Record<string, EventHandler>) {
      if (!this.$events) {
         this.$events = {};
      }

      for (const type in events) {
         this.addEvent(type, events[type]);
      }
      return this;
   },

   fireEvent(this: any, type: string, args?: any, delay?: number) {
      if (!this.$events) {
         return this;
      }

      type = removeOn(type);
      const events: EventHandlerWithInternal[] | undefined = this.$events[type];
      if (!events) {
         return this;
      }
      const argsArray = toArray(args);

      // Iterate with index check — removeEvent uses `delete` which creates
      // sparse slots, so we must skip holes.
      for (let i = 0; i < events.length; i++) {
         if (!(i in events)) continue;
         const fn = events[i];
         if (delay) {
            setTimeout(() => fn.apply(this, argsArray), delay);
         } else {
            fn.apply(this, argsArray);
         }
      }
      return this;
   },

   removeEvent(this: any, type: string, fn: EventHandlerWithInternal) {
      if (!this.$events) {
         return this;
      }

      type = removeOn(type);
      const events: EventHandlerWithInternal[] | undefined = this.$events[type];
      if (events && !fn.internal) {
         const index = events.indexOf(fn);
         if (index !== -1) {
            delete events[index];
         }
      }
      return this;
   },

   removeEvents(this: any, events?: string | Record<string, EventHandler>) {
      if (!this.$events) {
         return this;
      }

      if (typeof events === 'object' && events !== null && !Array.isArray(events)) {
         for (const type in events) {
            this.removeEvent(type, events[type]);
         }
         return this;
      }

      let filterType: string | undefined;
      if (events) {
         filterType = removeOn(events as string);
      }

      for (const type in this.$events) {
         if (filterType && filterType !== type) {
            continue;
         }
         const fns: EventHandlerWithInternal[] = this.$events[type];
         for (let i = fns.length; i--; ) {
            if (i in fns) {
               this.removeEvent(type, fns[i]);
            }
         }
      }
      return this;
   },
};

/**
 * Forwards an event from one EventsFunctions-powered object to another.
 *
 * When `from` fires `eventName`, `to` will fire the same event with the
 * same arguments.
 */
export function passEvent(eventName: string, from: any, to: any): void {
   from.addEvent(eventName, (...args: any[]) => {
      to.fireEvent(eventName, args);
   });
}
